import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router";
import { API_URL, NETWORK_DOWN_MESSAGE } from "@/lib/api";
import { safeLoginNext } from "@/lib/login-next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// Feature bullet points shown on the branded left panel
const features = [
  "AI-powered receipt scanning",
  "Client management dashboard",
  "Tax-time ready exports",
];

export function Login() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [uiState, setUiState] = useState<"loading" | "form" | "error">(
    "loading"
  );
  const [errorKind, setErrorKind] = useState<"generic" | "network">("generic");
  const [email, setEmail] = useState("");
  const [linkSent, setLinkSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expiredBannerVisible, setExpiredBannerVisible] = useState(
    searchParams.get("expired") === "1"
  );
  // Where to go after logging in: the accept page of a client's invitation, when
  // that is what sent the accountant here. Carried through the emailed link.
  const nextPath = safeLoginNext(searchParams.get("next"));

  useEffect(() => {
    const sesamePk = searchParams.get("sesame");
    if (sesamePk) {
      // Every parameter goes on, `next` included: the backend sends the
      // accountant to it after the login.
      window.location.href = `${API_URL}/api/accountant/auth/login?${searchParams.toString()}`;
      return;
    }

    fetch(`${API_URL}/api/accountant/me`, { credentials: "include" })
      .then((res) => {
        if (res.ok) {
          if (nextPath) {
            // The accept page is served by the API host, not this app.
            window.location.replace(`${API_URL}${nextPath}`);
          } else {
            navigate("/accountant/dashboard", { replace: true });
          }
        } else {
          setUiState("form");
        }
      })
      .catch(() => setUiState("form"));
  }, [searchParams, navigate, nextPath]);

  const handleRequestLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/accountant/auth/request-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(nextPath ? { email, next: nextPath } : { email }),
      });
      if (res.ok) {
        setLinkSent(true);
      } else {
        setErrorKind(res.status >= 502 && res.status <= 504 ? "network" : "generic");
        setUiState("error");
      }
    } catch {
      setErrorKind("network");
      setUiState("error");
    } finally {
      setSubmitting(false);
    }
  };

  // Full-screen loading state — minimal, brand-consistent
  if (uiState === "loading") {
    return (
      <div className="flex min-h-svh items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">
          <img src="/taxr-logo.png" alt="Taxr" className="h-9 w-auto animate-pulse" />
          <p className="text-sm text-muted-foreground tracking-wide">
            Signing you in...
          </p>
        </div>
      </div>
    );
  }

  // Success state — link sent
  if (linkSent) {
    return (
      <div className="flex min-h-svh items-center justify-center bg-white px-4">
        <div className="w-full max-w-sm text-center space-y-6">
          {/* Checkmark circle */}
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-highlight-muted text-highlight">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-8 w-8"
              aria-hidden="true"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              Check your email
            </h1>
            <p className="text-sm text-muted-foreground leading-relaxed">
              If{" "}
              <strong className="text-foreground font-medium">{email}</strong>{" "}
              has accountant portal access, we've sent a secure login
              link. The link expires in 7 days.
              <br />
              <br />
              Don't see it? Your portal may not be turned on yet.
              Email{" "}
              <a
                href="mailto:support@taxr.io"
                className="text-foreground underline underline-offset-4 decoration-muted-foreground/40 hover:decoration-foreground transition-colors"
              >
                support@taxr.io
              </a>{" "}
              and we'll set it up.
            </p>
          </div>

          <button
            type="button"
            onClick={() => {
              setLinkSent(false);
              setUiState("form");
            }}
            className="text-sm text-foreground underline underline-offset-4 decoration-muted-foreground/40 hover:decoration-foreground transition-colors"
          >
            Didn't get it? Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-svh">
      {/* ── Left brand panel (desktop only) ── */}
      <div
        className="hidden lg:flex lg:w-[44%] flex-col justify-between p-12 relative overflow-hidden bg-[#0a0a0a]"
        aria-hidden="true"
      >
        {/* Logo */}
        <div className="relative z-10 flex items-center gap-3">
          <img src="/taxr-logo.png" alt="Taxr logo" className="h-8 w-auto" />
          <span className="text-white/90 font-semibold text-lg tracking-tight">
            Taxr
          </span>
        </div>

        {/* Main copy */}
        <div className="relative z-10 space-y-8">
          <div className="space-y-4">
            <h2 className="text-3xl font-semibold leading-snug text-white">
              The expense tracker built for accountants and bookkeepers
            </h2>
            <p className="text-base text-white/60 leading-relaxed">
              Keep your clients' receipts in one place. Save hours at tax time.
            </p>
          </div>

          <ul className="space-y-3">
            {features.map((f) => (
              <li key={f} className="flex items-center gap-3">
                <span
                  className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/10 text-white/90"
                  aria-hidden="true"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-3 w-3"
                    aria-hidden="true"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>
                <span className="text-sm text-white/75">{f}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Bottom attribution */}
        <p className="relative z-10 text-xs text-white/30">
          &copy; {new Date().getFullYear()} Taxr Pty Ltd
        </p>
      </div>

      {/* ── Right form panel ── */}
      <div className="flex flex-1 flex-col items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-[400px] space-y-8">
          {/* Mobile logo (hidden on desktop where the left panel shows) */}
          <div className="flex items-center gap-2.5 lg:hidden">
            <img src="/taxr-logo.png" alt="Taxr logo" className="h-7 w-auto" />
            <span className="font-semibold text-base tracking-tight text-foreground">
              Taxr
            </span>
          </div>

          {/* Header */}
          <div className="space-y-1.5">
            <p className="text-xs font-medium uppercase tracking-widest text-highlight">
              Accountant Portal
            </p>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              Welcome back
            </h1>
            <p className="text-sm text-muted-foreground">
              Sign in to continue to your portal
            </p>
          </div>

          {/* Expired session banner */}
          {expiredBannerVisible && (
            <div
              role="alert"
              className="flex items-start justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
            >
              <div className="flex items-start gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mt-0.5 h-4 w-4 shrink-0 text-amber-600"
                  aria-hidden="true"
                >
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                <span>
                  Your login link has expired. Request a new one below.
                </span>
              </div>
              <button
                type="button"
                onClick={() => setExpiredBannerVisible(false)}
                className="shrink-0 text-amber-600 hover:text-amber-800 transition-colors"
                aria-label="Dismiss"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-4 w-4"
                  aria-hidden="true"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          )}

          {/* Error state inline */}
          {uiState === "error" && (
            <div
              role="alert"
              className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
            >
              {errorKind === "network"
                ? NETWORK_DOWN_MESSAGE
                : "Something went wrong. Please try again."}
            </div>
          )}

          {/* Form */}
          <form
            onSubmit={handleRequestLink}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.currentTarget.requestSubmit();
              }
            }}
            className="space-y-5"
            noValidate
          >
            <div className="space-y-2">
              <Label htmlFor="login-email" className="text-sm font-medium">
                Email address
              </Label>
              <Input
                id="login-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (uiState === "error") setUiState("form");
                }}
                placeholder="jane@example.com"
                required
                className="h-10 text-sm"
                aria-required="true"
              />
            </div>

            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={submitting}
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <svg
                    className="h-4 w-4 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8H4z"
                    />
                  </svg>
                  Sending...
                </span>
              ) : (
                "Send login link"
              )}
            </Button>

            <p className="text-center text-xs text-muted-foreground leading-relaxed">
              If you have accountant portal access, we'll email you a
              secure, one-time login link.
              <br />
              No password needed.
            </p>
          </form>

          {/* Request access — the front door for accountants who have never
              had an account. Previously a footnote below "Need help?", where
              it read as fine print rather than an action. */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center" aria-hidden>
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-background px-2 text-xs text-muted-foreground">
                New here?
              </span>
            </div>
          </div>

          <div className="space-y-2 text-center">
            <p className="text-sm text-muted-foreground">
              Don't have portal access yet?
            </p>
            <Link
              to="/request-access"
              className="flex h-10 w-full items-center justify-center rounded-md border border-input bg-transparent px-4 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              Request an invite
            </Link>
          </div>

          {/* Footer */}
          <div className="space-y-2">
            <p className="text-center text-xs text-muted-foreground">
              Need help?{" "}
              <a
                href="mailto:support@taxr.io"
                className="text-foreground underline underline-offset-4 decoration-muted-foreground/40 hover:decoration-foreground transition-colors"
              >
                Contact support
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
